import { useI18n } from '@fox/extensions';
import { FlexRowSmallSpaced } from '@fox/layout/src/flex';
import React from 'react';
import styled, { useTheme } from 'styled-components';
import { strIsNullOrEmpty } from '../../utils/string';
import { Chip } from '../chip/index';
import { Icon } from '../icon/index';
import { Tooltip } from '../tooltip';
import { Typography } from '../typography/index';
import { FieldRenderMode } from './contracts';

const FieldLabelWrapper = styled.div`
	display: flex;
	justify-content: space-between;
`;

const IconWrapper = styled.div`
	opacity: 0;
	transition: 0.25s all linear;
	position: absolute;
	right: 0.25rem;
	top: 0.5rem;
	cursor: pointer;
`;

export const FieldErrorWrapper = styled.div`
	color: ${props => props.theme.palette.error.main};
	text-align: left;
	font-size: 0.75rem;
	height: 1rem;
	padding-left: 0.5rem;
`;

const FieldInfoWrapper = styled.div`
	color: ${props => props.theme.palette.info.main};
	text-align: left;
	font-size: 0.75rem;
	height: 1rem;
	padding-left: 0.5rem;
`;

const FlexStartWrapper = styled.div`
	height: 100%;
	width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	display: flex;
	align-items: flex-start;
`;
type FieldStyleProps = {
	$renderMode: FieldRenderMode;
	$borderColor?: string;
	$noPadding?: boolean;
	$error?: boolean;
	$noHeight?: boolean;
	$noFlex?: boolean;
	$hasClearIcon?: boolean;
	$customHeight?: string;
};

const FieldStyleWrapperDiv = styled.div<FieldStyleProps>`
	color: ${props => props.theme.palette.text.primary};
	background-color: unset;
	background: none;
	transition: all 0.3s ease-in-out;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	position: relative;

	${props => (!props.$noFlex ? 'display: flex; align-items: center; flex: 1;' : '')}

	${props => (!props.$noHeight ? `height: ${props.$customHeight ?? '40px'};` : '')}

	${props =>
		props.$renderMode === FieldRenderMode.GridEdit &&
		`border: 1px solid ${
			props.$error
				? props.theme.palette.error.main
				: props.$borderColor
					? props.$borderColor
					: props.theme.palette.swiftfox.neutral['50-midGrey']
		}; border-radius: 3px;`};
	${props =>
		(props.$renderMode === FieldRenderMode.TableEdit || props.$renderMode === FieldRenderMode.TableReadonly) &&
		'border: none; border-radius: none;'};
	${props => !props.$noPadding && 'padding: 0.25em 0.5em;'};

	&:focus-within {
		border-color: ${props => (props.$borderColor ? props.$borderColor : props.theme.palette.primary.main)};
	}
	&:hover {
		border-color: ${props => (props.$borderColor ? props.$borderColor : props.theme.palette.primary.main)};
		${IconWrapper} {
			${props => (props.$hasClearIcon ? 'opacity: 0.75;' : '')}
		}
	}

	fieldset {
		border: none !important;
	}

	.select-input-wrap {
		border: none;
	}
`;

const FieldStyleWrapper = ({
	fieldProps,
	fieldHistoryData,
	children
}: {
	fieldProps: FieldStyleProps & { style?: React.CSSProperties };
	fieldHistoryData?: {
		lastUpdated?: string;
		updatedBy?: string;
		previousValue?: string;
	};
	children: React.ReactNode;
}) => {
	const { locale } = useI18n();
	if (!fieldHistoryData || !fieldHistoryData?.lastUpdated || !fieldHistoryData.updatedBy) {
		return <FieldStyleWrapperDiv {...fieldProps}>{children}</FieldStyleWrapperDiv>;
	}
	return (
		<Tooltip
			placement="top"
			title={
				<Typography>
					Last updated on{' '}
					<Typography component="span" fontWeight="bold">
						{new Date(fieldHistoryData.lastUpdated).toLocaleDateString(locale)}
					</Typography>{' '}
					by{' '}
					<Typography component="span" fontWeight="bold">
						{fieldHistoryData.updatedBy}
					</Typography>
					{!strIsNullOrEmpty(fieldHistoryData?.previousValue) ? ' from ' : ''}
					<Typography component="span" fontWeight="bold">
						{!strIsNullOrEmpty(fieldHistoryData?.previousValue) ? `${fieldHistoryData?.previousValue}` : ''}
					</Typography>
				</Typography>
			}
		>
			<FieldStyleWrapperDiv {...fieldProps}>{children}</FieldStyleWrapperDiv>
		</Tooltip>
	);
};

const FieldWrapper = styled.div<{ $renderMode: FieldRenderMode }>`
	${props => (props.$renderMode === FieldRenderMode.GridEdit || FieldRenderMode.GridReadonly) && 'display: grid; grid-template-columns:  1fr;'}
`;

const FieldHeaderStyled = styled.div`
	display: flex;
	align-items: 'center';
	padding: 0.25em 0em;
	color: ${props => props.theme.palette.swiftfox.neutral['50-midGrey']};
	font-family: ${props => props.theme.typography?.fontFamily};
	font-size: ${props => props.theme.typography?.fontSize};
`;

export const FieldHeader: React.FC<React.PropsWithChildren> = ({ children }) => <FieldHeaderStyled>{children}</FieldHeaderStyled>;

export type RenderFieldWrapperStyleProps = {
	borderColor?: string;
	noPadding?: boolean;
	noHeight?: boolean;
	customHeight?: string;
};

export type RenderFieldWrapperProps = {
	children: React.ReactNode;
	renderMode: FieldRenderMode;
	chipLabel?: string;
	label?: string;
	labelPrefixDecoration?: React.ReactNode;
	error?: string;
	info?: string;
	clearIconCallback?: () => void;
	hideErrorWrapper?: boolean;
	fieldHistoryData?: {
		lastUpdated?: string;
		updatedBy?: string;
		previousValue?: string;
	};
} & RenderFieldWrapperStyleProps;

/** This wrapper doesn't play nicely with the new TextField. */
export const RenderFieldWrapper = (props: RenderFieldWrapperProps) => {
	const theme = useTheme();
	return (
		<FieldWrapper $renderMode={props.renderMode}>
			<FieldLabelWrapper>
				{props.label ? (
					props.labelPrefixDecoration ? (
						<FlexRowSmallSpaced>
							{props.labelPrefixDecoration}
							<FieldHeader>{props.label}</FieldHeader>
						</FlexRowSmallSpaced>
					) : (
						<FieldHeader>{props.label}</FieldHeader>
					)
				) : (
					<></>
				)}
				{props.chipLabel ? <Chip color="primary" label={props.chipLabel} /> : null}
			</FieldLabelWrapper>
			{props.renderMode === FieldRenderMode.GridReadonly ? (
				<FieldStyleWrapper
					fieldProps={{
						$noHeight: props.noHeight,
						$customHeight: props.customHeight,
						$renderMode: props.renderMode,
						$borderColor: props.borderColor,
						$noPadding: props.noPadding,
						$error: !strIsNullOrEmpty(props.error)
					}}
					fieldHistoryData={props.fieldHistoryData}
				>
					<FlexStartWrapper>{props.children}</FlexStartWrapper>
				</FieldStyleWrapper>
			) : (
				<FieldStyleWrapper
					fieldProps={{
						$noHeight: props.noHeight,
						$customHeight: props.customHeight,
						$renderMode: props.renderMode,
						$borderColor: props.borderColor,
						$noPadding: props.noPadding,
						$error: !strIsNullOrEmpty(props.error),
						$hasClearIcon: props.clearIconCallback !== undefined
					}}
				>
					<>
						{props.children}
						{props.clearIconCallback !== undefined ? (
							<IconWrapper onClick={props.clearIconCallback}>
								<Icon iconName="HighlightOff" sx={{ color: theme.palette.text.primary }} />
							</IconWrapper>
						) : null}
					</>
				</FieldStyleWrapper>
			)}
			{!props.hideErrorWrapper ? (
				!props.info || props.error ? (
					<FieldErrorWrapper>{props.error}</FieldErrorWrapper>
				) : (
					<FieldInfoWrapper>{props.info}</FieldInfoWrapper>
				)
			) : (
				<></>
			)}
		</FieldWrapper>
	);
};

export const MultiFieldWrapper = (props: {
	children: React.ReactNode;
	renderMode: FieldRenderMode.GridEdit | FieldRenderMode.GridReadonly;
	borderColor?: string;
	noPadding?: boolean;
	label?: string;
	error?: string;
	info?: string;
	hideErrorWrapper?: boolean;
}) => {
	return (
		<FieldWrapper $renderMode={props.renderMode}>
			{props.label ? (
				<Typography style={{ display: 'flex', alignItems: 'center', padding: '0.25em 0em', fontSize: '0.95rem' }}>{props.label}</Typography>
			) : (
				<></>
			)}

			<FieldStyleWrapper
				fieldProps={{
					$renderMode: props.renderMode,
					$borderColor: props.borderColor,
					$noPadding: props.noPadding,
					$error: !strIsNullOrEmpty(props.error),
					$noFlex: true,
					$noHeight: true,
					$hasClearIcon: false,
					style: { paddingBottom: '0.5rem' }
				}}
			>
				{props.children}
			</FieldStyleWrapper>

			{!props.hideErrorWrapper ? (
				!props.info || props.error ? (
					<FieldErrorWrapper>{props.error}</FieldErrorWrapper>
				) : (
					<FieldInfoWrapper>{props.info}</FieldInfoWrapper>
				)
			) : (
				<></>
			)}
		</FieldWrapper>
	);
};
